"use client";

import {
  useMemo,
  useId,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useParams, useSearchParams } from "next/navigation";
import ProductPicture from "./_components/ProductPicture";
import ProductInfo from "./_components/ProductInfo";
import LinkingProcess from "./_components/LinkingProcess";
import TopBar from "./_components/TopBar";
import BottomBar from "./_components/BottomBar";
import { clientDocument } from "@/utils/dom";
import dynamic from "next/dynamic";
import { useTrack } from "@/hooks/useTrack";
import { TrackingPoints } from "./points";
import "./index.scss";
import { ResponseData, TProductInfo, TShopDetail } from "@/typings";

const Locator = dynamic(() => import("./_components/Locator"), {
  loading: () => null,
});
const Overview = dynamic(() => import("./_components/Overview"), {
  loading: () => null,
});
const ShopInfo = dynamic(() => import("./_components/ShopInfo"), {
  loading: () => null,
});
const SoldTrend = dynamic(() => import("./_components/SoldTrend"), {
  loading: () => null,
});
const HotRecommendCreator = dynamic(
  () => import("./_components/HotRecommendCreator"),
  { loading: () => null }
);
const HotRecommendVideo = dynamic(
  () => import("./_components/HotRecommendVideo"),
  { loading: () => null }
);
const HotRecommendLive = dynamic(
  () => import("./_components/HotRecommendLive"),
  { loading: () => null }
);
const ProductDescription = dynamic(
  () => import("./_components/ProductDescription"),
  { loading: () => null }
);
const ShopThresholds = dynamic(() => import("./_components/ShopThresholds"), {
  loading: () => null,
});
const RelatedProducts = dynamic(() => import("./_components/RelatedProducts"), {
  loading: () => null,
});

const classPrefix = "yfm-product-detail-content";

interface ProductPageContentProps {
  productDetailResponse?: ResponseData<TProductInfo>;
  shopDetailResponse?: ResponseData<TShopDetail>;
}

export default function ProductPageContent(props: ProductPageContentProps) {
  const { productDetailResponse, shopDetailResponse } = props;

  const { tracking } = useTrack();
  const id = useId();
  const checkTimerRef = useRef<NodeJS.Timeout | null>(null);
  const params = useParams<{ hashId: string }>();
  const searchParams = useSearchParams();
  const isYfChannel = searchParams.get("channel_code") === "yf_admin";
  const topBarRef = useRef<HTMLDivElement>(null);
  const [offsetTop, setOffsetTop] = useState(0);
  const checkOffsetTopCount = useRef(0);
  const [emptyIds, setEmptyIds] = useState<string[]>([]);
  // const { data: productDetailResponse, status } = useProductDetail(
  //   params.hashId
  // );
  // const isSkeleton = status !== "success" && status !== "error";

  const isSkeleton = false;

  const ids = useMemo(
    () => ({
      overview: `overview-${id}`,
      trend: `trend-${id}`,
      relatedCreators: `related-creators-${id}`,
      relatedVideos: `related-videos-${id}`,
      relatedLives: `related-lives-${id}`,
      productDetails: `product-details-${id}`,
      shopThresholds: `shop-thresholds-${id}`,
      linkingProcess: `linking-process-${id}`,
      similarProducts: `similar-products-${id}`,
    }),
    [id]
  );

  const locators = useMemo(
    () => [
      {
        id: ids.overview,
        title: "Overview",
      },
      {
        id: ids.productDetails,
        title: "Product Details",
      },
      {
        id: ids.trend,
        title: "Units Sold Trend",
        hide: emptyIds.includes(ids.trend),
      },
      {
        id: ids.relatedCreators,
        title: "Product Related Creators",
        hide: emptyIds.includes(ids.relatedCreators),
      },
      {
        id: ids.relatedVideos,
        title: "Product Related Videos",
        hide: emptyIds.includes(ids.relatedVideos),
      },
      {
        id: ids.relatedLives,
        title: "Product Related Lives",
        hide: emptyIds.includes(ids.relatedLives),
      },
      {
        id: ids.shopThresholds,
        title: "Shop Thresholds",
      },
      {
        id: ids.linkingProcess,
        title: "Linking Process",
        hide: isYfChannel,
      },
      {
        id: ids.similarProducts,
        title: "Similar Products",
        hide: isYfChannel,
      },
    ],
    [isYfChannel, ids, emptyIds]
  );

  const productInfo = useMemo(() => {
    if (productDetailResponse?.code === 0) {
      return productDetailResponse.data;
    }
    return null;
  }, [productDetailResponse]);

  const categoryId = useMemo(() => {
    return productInfo?.category_ids?.first_category_id ?? 0;
  }, [productInfo]);

  // const shopId = useMemo(() => {
  //   if (productInfo?.shop_id) {
  //     return productInfo.shop_id;
  //   }
  //   return "";
  // }, [productInfo]);

  // const { data: shopDetailResponse } = useShopDetail(shopId as string);

  const shopInfo = useMemo(() => {
    if (shopDetailResponse?.code === 0) {
      return shopDetailResponse.data;
    }
    return null;
  }, [shopDetailResponse]);

  const addEmptyId = useCallback((id: string) => {
    setEmptyIds((prev) => [...prev, id]);
  }, []);

  useEffect(() => {
    if (productInfo?.product_id) {
      tracking({
        ...TrackingPoints.Exposure,
        product_id: productInfo?.product_id,
      });
    }
  }, [productInfo, tracking]);

  useEffect(() => {
    // 初始化 1s 后检测顶部条，为 Locator 设置 offsetTop，最多检测 6 次
    const MAX_TIMES = 6;
    const DELAY = 1000;
    const check = (delay: number) => {
      checkTimerRef.current = setTimeout(() => {
        checkOffsetTopCount.current++;
        // console.log(checkOffsetTopCount.current, topBarRef.current);
        if (topBarRef.current) {
          setOffsetTop(topBarRef.current.offsetHeight);
        } else if (checkOffsetTopCount.current <= MAX_TIMES) {
          check(delay);
        }
      }, delay);
    };
    check(DELAY);
    // 页面尺寸变化，也检测一次顶部条
    const documentElement = clientDocument?.documentElement;
    if (!documentElement) return;
    const observer = new ResizeObserver(() => {
      if (topBarRef.current) {
        setOffsetTop(topBarRef.current.offsetHeight);
      }
    });
    observer.observe(documentElement);
    return () => {
      observer.disconnect();
      if (checkTimerRef.current) {
        clearTimeout(checkTimerRef.current);
        checkTimerRef.current = null;
      }
    };
  }, []);

  return (
    <div
      className={`${classPrefix}__wrapper ${isYfChannel ? "no-padding" : ""}`}
    >
      {!isYfChannel && (
        <TopBar
          ref={topBarRef}
          isSkeleton={isSkeleton}
          hashId={params.hashId}
          productId={productInfo?.product_id}
        />
      )}
      <ProductPicture
        images={productInfo?.images || []}
        productInfo={productInfo}
        hashId={params.hashId}
      />
      <div className={`${classPrefix}__body`}>
        <ProductInfo productInfo={productInfo} />
        {!isSkeleton && (
          <div className={`${classPrefix}__card`}>
            <Locator locatorList={locators} offsetTop={offsetTop} />
            <Overview id={ids.overview} productInfo={productInfo} />
            <ShopInfo shopInfo={shopInfo} />
            <ProductDescription
              id={ids.productDetails}
              description={productInfo?.description}
            />
            <SoldTrend
              id={ids.trend}
              productInfo={productInfo}
              addEmptyId={addEmptyId}
            />
            <HotRecommendCreator
              id={ids.relatedCreators}
              productId={productInfo?.product_id}
              addEmptyId={addEmptyId}
            />
            <HotRecommendVideo
              id={ids.relatedVideos}
              productId={productInfo?.product_id}
              hashId={params.hashId}
              addEmptyId={addEmptyId}
            />
            <HotRecommendLive
              id={ids.relatedLives}
              productId={productInfo?.product_id}
              addEmptyId={addEmptyId}
            />
          </div>
        )}
        <ShopThresholds id={ids.shopThresholds} />
        {!isYfChannel && (
          <LinkingProcess
            isSkeleton={isSkeleton}
            id={ids.linkingProcess}
            hashId={params.hashId}
          />
        )}
        {!isSkeleton && !isYfChannel && (
          <RelatedProducts id={ids.similarProducts} categoryId={categoryId} />
        )}
      </div>
      {!isYfChannel && <BottomBar productInfo={productInfo} />}
    </div>
  );
}
